import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Scale, GraduationCap, Award, Microscope,
  Search, UserCheck, ShieldCheck, Brain, ArrowRight
} from 'lucide-react';

const modules = [
  {
    id: 1, icon: Scale, title: 'SISTEMA',
    description: 'Gestão completa de processos periciais, honorários e laudos.',
    route: '/sistema', color: '#193D6D', accent: '#2A5487'
  },
  {
    id: 2, icon: GraduationCap, title: 'ACADEMIA',
    description: 'Cursos, treinamentos e capacitação para peritos judiciais.',
    route: '/academia', color: '#0369A1', accent: '#0C4A6E'
  },
  {
    id: 3, icon: Award, title: 'CERTIFICAÇÃO',
    description: 'Certificação profissional e validação de competências.',
    route: '/certificacao', color: '#B45309', accent: '#92400E'
  },
  {
    id: 4, icon: Microscope, title: 'LABORATÓRIO',
    description: 'Ferramentas de análise documental e grafotécnica com IA.',
    route: '/laboratorio', color: '#0F766E', accent: '#115E59'
  },
  {
    id: 5, icon: Search, title: 'PESQUISA',
    description: 'Bases de dados, jurisprudência e referências técnicas.',
    route: '/pesquisa', color: '#7C3AED', accent: '#6D28D9'
  },
  {
    id: 6, icon: UserCheck, title: 'ASSISTENTE TÉCNICO',
    description: 'Módulo especializado para assistentes técnicos das partes.',
    route: '/assistente-tecnico', color: '#0891B2', accent: '#0E7490'
  },
  {
    id: 7, icon: ShieldCheck, title: 'COMPLIANCE',
    description: 'Auditoria, rastreabilidade e conformidade regulatória.',
    route: '/compliance', color: '#059669', accent: '#047857'
  },
  {
    id: 8, icon: Brain, title: 'INTELIGÊNCIA FORENSE',
    description: 'Análise preditiva e IA aplicada à investigação forense.',
    route: '/inteligencia-forense', color: '#DC2626', accent: '#B91C1C'
  },
];

function ModuleCard({ icon: Icon, title, description, route, color, accent, index }) {
  return (
    <Link
      to={route}
      className="group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{ '--card-color': color, '--card-accent': accent, focusRingColor: color }}
      aria-label={`Acessar módulo ${title}`}
      id={`module-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Card Background with gradient */}
      <div className="absolute inset-0 transition-all duration-500 opacity-100 group-hover:opacity-0"
        style={{ background: `linear-gradient(135deg, ${color} 0%, ${accent} 100%)` }} />
      <div className="absolute inset-0 transition-all duration-500 opacity-0 group-hover:opacity-100"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${color} 100%)` }} />

      {/* Decorative circle */}
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 bg-white" aria-hidden="true" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-[0.05] bg-white" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 p-6 flex flex-col flex-1 text-white"
        style={{ animation: `fadeIn ${0.3 + index * 0.08}s ease-out` }}>
        <div className="flex items-center justify-between mb-5">
          <div className="p-3 rounded-xl bg-white/15 backdrop-blur-sm border border-white/10 shadow-inner">
            <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
          </div>
          <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white/90 group-hover:translate-x-1 transition-all duration-300" />
        </div>
        <h3 className="text-[15px] font-bold tracking-[0.1em] uppercase mb-2 text-white">
          {title}
        </h3>
        <p className="text-[12px] leading-relaxed text-white/70 group-hover:text-white/90 transition-colors mt-auto">
          {description}
        </p>
      </div>

      {/* Gold bottom accent */}
      <div className="relative z-10 h-1 w-0 group-hover:w-full bg-[#FCD34D] transition-all duration-500 ease-out" />
    </Link>
  );
}

export default function ModuleGrid() {
  const { currentLanguage } = useTranslation();
  const getPath = (p) => `/${currentLanguage}${p}`;

  return (
    <div
      className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      id="modules"
      role="navigation"
      aria-label="Módulos da plataforma"
    >
      {modules.map((mod, index) => (
        <ModuleCard
          key={mod.id}
          {...mod}
          route={getPath(mod.route)}
          index={index}
        />
      ))}
    </div>
  );
}